import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { AuthService, createAuthService, onAuthEvent, clearAllAuthData, hasPendingOAuthCode } from '../services/authService';
import {
  initializePlatformApi,
  getCurrentUser,
  createContext as createPlatformContext,
  setSessionAuthToken,
  IafPassSvc
} from '../services/platformApi';
import type { User, PlatformContext, Application } from '../types/config';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  authService: AuthService | null;
  accessToken: string | null;
  application: Application | null;
  baseNamespaces: string[];
  login: (clientId: string, inviteId?: string) => Promise<void>;
  logout: () => Promise<void>;
  setClientId: (clientId: string) => void;
  createCtx: (additionalNamespaces?: string[]) => PlatformContext;
  handleUnauthorized: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  initialClientId?: string;
}

export function AuthProvider({ children, initialClientId }: AuthProviderProps) {
  const CLIENT_ID_STORAGE_KEY = 'platform_app_clientId';
  const [authService, setAuthService] = useState<AuthService | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [clientId, setClientIdState] = useState<string | null>(initialClientId || null);
  const [application, setApplication] = useState<Application | null>(null);
  const [baseNamespaces, setBaseNamespaces] = useState<string[]>([]);
  // Serialize init across re-renders/StrictMode
  const initInProgressRef = useRef(false);
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const initAttemptIdRef = useRef(0);
  // Track previous clientId to detect actual changes vs initial load
  const previousClientIdRef = useRef<string | null>(null);

  // Initialize platform API on mount
  useEffect(() => {
    initializePlatformApi();
  }, []);

  // Restore clientId from localStorage after redirects/hard refresh
  useEffect(() => {
    if (clientId) return;
    try {
      const stored = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (stored) {
        setClientIdState(stored);
        // eslint-disable-next-line no-console
        console.debug('[Auth] Restored clientId from storage:', stored);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] Failed to restore clientId from storage', e);
    }
  }, [clientId]);

  // Listen for auth events (token refresh, auth failed)
  useEffect(() => {
    const unsubscribe = onAuthEvent((event) => {
      if (event === 'tokenRefreshed' && authService) {
        // Update access token when refreshed
        const newToken = authService.getAccessToken();
        setAccessToken(newToken);
        // Update IafSession with the new token
        if (newToken) {
          setSessionAuthToken(newToken);
        }
        console.log('Access token updated after refresh');
      } else if (event === 'authFailed') {
        // Clear auth state
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
      } else if (event === 'logout') {
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
        setApplication(null);
        setBaseNamespaces([]);
      }
    });

    return unsubscribe;
  }, [authService]);

  // Initialize auth service when clientId changes
  useEffect(() => {
    if (!clientId) {
      setIsLoading(false);
      return;
    }

    // Persist clientId so it survives authorize redirects/hard reloads
    try {
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] Failed to persist clientId to storage', e);
    }

    // IMPORTANT: Only clear tokens when SWITCHING apps (clientId actually changed),
    // not on initial page load or refresh where we're restoring the same clientId.
    const isClientIdChange = previousClientIdRef.current !== null &&
                             previousClientIdRef.current !== clientId;

    if (isClientIdChange) {
      // Client ID actually changed - clear old tokens to avoid cross-app token reuse
      // BUT if an OAuth code is present in the URL OR an exchange is in progress,
      // do NOT clear PKCE or pre-auth URI; AuthService.initialize() needs those.
      try {
        const exchangeInProgress = (() => {
          try { return sessionStorage.getItem('oauth_exchange_in_progress') === '1'; } catch { return false; }
        })();
        if (hasPendingOAuthCode() || exchangeInProgress) {
          // eslint-disable-next-line no-console
          console.debug('[Auth] Client ID changed but OAuth code detected – only clearing access token');
          localStorage.removeItem('dt_platform_auth');
        } else {
          localStorage.removeItem('dt_platform_auth');
          localStorage.removeItem('dt_platform_pkce');
          localStorage.removeItem('dt_platform_pre_auth_uri');
          // eslint-disable-next-line no-console
          console.debug('[Auth] Client ID changed – cleared tokens/PKCE (client id preserved)');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Auth] Failed to clear tokens when switching clients', e);
      }
    } else {
      // eslint-disable-next-line no-console
      console.debug('[Auth] Initializing with existing client ID – preserving tokens');
    }

    // Update ref for next time
    previousClientIdRef.current = clientId;

    const service = createAuthService(clientId);
    setAuthService(service);

    const initAuth = async () => {
      // Serialize init to avoid duplicate initialize()/exchanges
      if (initInProgressRef.current && initPromiseRef.current) {
        // eslint-disable-next-line no-console
        console.debug('[Auth.init] init already in progress – joining existing promise');
        await initPromiseRef.current;
        return;
      }
      initInProgressRef.current = true;
      const attempt = ++initAttemptIdRef.current;
      // eslint-disable-next-line no-console
      console.debug(`[Auth.init] start (attempt ${attempt})`);
      const initPromise = (async () => {
      setIsLoading(true);
      try {
        const authenticated = await service.initialize();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const token = service.getAccessToken();
          setAccessToken(token);

          if (token) {
            // Set the token in IafSession for ScriptHelper and ipa-core utilities
            setSessionAuthToken(token);
            let namespaces: string[] = [];

            // First, try to get the application to retrieve its namespaces
            try {
              const appCtx = createPlatformContext(token, []);
              const app = await IafPassSvc.getApplication(clientId, appCtx);

              if (app) {
                setApplication(app);
                namespaces = [...(app._namespaces || [])];
                console.log('Application loaded:', app._name, 'Namespaces:', namespaces);
              }
            } catch (error: unknown) {
              // Check if it's a 401 - token is invalid
              const is401 = isUnauthorizedError(error);

              if (is401) {
                console.warn('Token is invalid (401), clearing auth state');
                service.forceReauth();
                setIsAuthenticated(false);
                setAccessToken(null);
                return; // Don't continue trying
              }

              console.warn('Could not fetch application details:', error);
              // Non-401 error - don't try fallback, just continue without app details
            }

            // Store base namespaces for use throughout the app
            setBaseNamespaces(namespaces);

            // Now fetch current user with proper namespaces
            const ctx = createPlatformContext(token, namespaces);
            try {
              // Add cache-busting to ensure fresh user data
              console.log('[Auth] Fetching current user with token:', token.substring(0, 20) + '...');
              const currentUser = await getCurrentUser(ctx);
              
              // Verify the user from token matches the API response
              const tokenUser = service.getUser();
              if (tokenUser && tokenUser.email && currentUser._email !== tokenUser.email) {
                console.warn('[Auth] Token user mismatch! Token:', tokenUser.email, 'API:', currentUser._email);
                console.warn('[Auth] This may indicate stale session cookies. Forcing re-auth...');
                service.forceReauth();
                setIsAuthenticated(false);
                setAccessToken(null);
                return;
              }
              
              setUser(currentUser);
              console.log('[Auth] Current user:', currentUser._email);
            } catch (error: unknown) {
              // Check if it's a 401 - token is invalid
              const is401 = isUnauthorizedError(error);

              if (is401) {
                console.warn('Token is invalid (401) when getting user, clearing auth state');
                service.forceReauth();
                setIsAuthenticated(false);
                setAccessToken(null);
                return;
              }

              console.error('Failed to fetch user:', error);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        initInProgressRef.current = false;
        initPromiseRef.current = null;
        // eslint-disable-next-line no-console
        console.debug(`[Auth.init] finished (attempt ${attempt})`);
      }
      })();
      initPromiseRef.current = initPromise;
      await initPromise;
    };

    // Helper to check for 401 errors
    function isUnauthorizedError(error: unknown): boolean {
      if (!error) return false;
      if (typeof error === 'object' && error !== null) {
        const err = error as Record<string, unknown>;
        if (err.status === 401 || err.statusCode === 401) return true;
        if (err.response && typeof err.response === 'object') {
          const response = err.response as Record<string, unknown>;
          if (response.status === 401) return true;
        }
        // Some endpoints return JSON error payloads
        if (typeof err.error === 'string' && err.error.toLowerCase() === 'invalid_token') return true;
        const desc = (err.error_description || err.message || '') as string;
        if (typeof desc === 'string' && desc.toLowerCase().includes('invalid access token')) return true;
      }
      if (error instanceof Error && error.message.toLowerCase().includes('401')) {
        return true;
      }
      return false;
    }

    initAuth();
  }, [clientId]);

  const login = useCallback(async (newClientId: string, inviteId?: string) => {
    // Reset any existing state to avoid cross-app token reuse
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setApplication(null);
    setBaseNamespaces([]);

    try {
      // Only clear tokens/PKCE while preserving client id keys
      localStorage.removeItem('dt_platform_auth');
      localStorage.removeItem('dt_platform_pkce');
      localStorage.removeItem('dt_platform_pre_auth_uri');
      // eslint-disable-next-line no-console
      console.debug('[Auth] Cleared auth tokens before login() (client id preserved)');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] Failed to clear auth tokens before login()', e);
    }

    setClientIdState(newClientId);
    try {
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, newClientId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] Failed to persist clientId on login', e);
    }
    const service = createAuthService(newClientId);
    setAuthService(service);
    await service.authorize(inviteId);
  }, []);

  const logout = useCallback(async () => {
    // Clear local state first
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setApplication(null);
    setBaseNamespaces([]);
    // Intentionally DO NOT clear stored clientId so re-entry after redirect works seamlessly
    // If you want to force user to pick app again on logout, uncomment the next lines.
    // try { localStorage.removeItem(CLIENT_ID_STORAGE_KEY); } catch {}
    
    // Clear any cached auth data from localStorage/sessionStorage
    try {
      // Clear common auth storage keys that might cache user data
      const keysToCheck = Object.keys(localStorage);
      keysToCheck.forEach(key => {
        if (key.includes('auth') || key.includes('token') || key.includes('user') || 
            key.includes('session') || key.includes('pkce')) {
          // Don't clear clientId to maintain app selection
          if (!key.includes('clientId')) {
            localStorage.removeItem(key);
          }
        }
      });
      sessionStorage.clear();
    } catch (e) {
      console.warn('[Auth] Could not clear storage:', e);
    }
    
    if (authService) {
      // Pass true to end session on passport service (OAuth standard logout)
      // This will redirect to the provider's logout endpoint
      await authService.logout(true);
    }
  }, [authService]);

  const setClientId = useCallback((newClientId: string) => {
    setClientIdState(newClientId);
    try {
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, newClientId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] Failed to persist clientId via setClientId', e);
    }
  }, []);

  // Create context with base namespaces + any additional namespaces passed
  const createCtx = useCallback((additionalNamespaces: string[] = []): PlatformContext => {
    // Combine additional namespaces with base namespaces, removing duplicates
    const allNamespaces = [...additionalNamespaces, ...baseNamespaces]
      .filter((ns, index, self) => self.indexOf(ns) === index);

    return createPlatformContext(accessToken || '', allNamespaces);
  }, [accessToken, baseNamespaces]);

  // Handle 401 unauthorized - try to refresh token
  const handleUnauthorized = useCallback(async (): Promise<boolean> => {
    if (!authService) {
      console.warn('No auth service available to handle unauthorized');
      return false;
    }
    return authService.handleUnauthorized();
  }, [authService]);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    authService,
    accessToken,
    application,
    baseNamespaces,
    login,
    logout,
    setClientId,
    createCtx,
    handleUnauthorized,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
